export const SITE_NAME = "UniUI";
export const SITE_URL =
  process.env.NEXT_PUBLIC_SITE_URL || "https://app.uniui.com.ng";
export const SITE_DESCRIPTION =
  "UniUI is a strict Socratic tutor for Nigerian university students. Ask anything from your course notes and get a verified, cited, step-by-step answer.";
export const DEFAULT_SOCIAL_IMAGE = "/UNIUI_BotFather_640x360.png";

export type MetadataPreset = {
  title: string;
  description: string;
  openGraph: {
    title: string;
    description: string;
    url?: string;
    siteName?: string;
    type?: string;
    image?: string;
    imageAlt?: string;
  };
  /** Page keywords, incl. the uniui-<page> slugs. */
  keywords?: string[];
  twitter?: {
    title?: string;
    description?: string;
    card?: string;
    image?: string;
  };
};

export const METADATA: Record<string, MetadataPreset> = {
  landing: {
    title: "UniUI – You Are Here to Learn.",
    description: SITE_DESCRIPTION,
    openGraph: {
      title: "UniUI – Socratic Tutor",
      description: SITE_DESCRIPTION,
      url: SITE_URL,
      siteName: SITE_NAME,
      type: "website",
    },
    twitter: {
      card: "summary_large_image",
      title: "UniUI – Socratic Tutor",
      description: SITE_DESCRIPTION,
    },
  },
  login: {
    keywords: [
      "uniui-login",
      "uniui login",
      "sign in uniui",
      "uniui-about",
      "uniui courses",
      "student login",
      "uniui.com.ng",
    ],
    title: "UniUI – Prove Your Identity.",
    description: "Sign in to UniUI to ask Socratic questions grounded in your own course notes, build a study streak, and get a verified step-by-step answer.",
    openGraph: {
      title: "UniUI – Prove Your Identity.",
      description: "Sign in to UniUI to ask Socratic questions grounded in your own course notes, build a study streak, and get a verified step-by-step answer.",
      url: `${SITE_URL}/auth/login`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  register: {
    keywords: [
      "uniui-register",
      "uniui register",
      "create uniui account",
      "uniui-about",
      "uniui courses",
      "free student account",
      "uniui.com.ng",
    ],
    title: "UniUI – Begin Your Journey.",
    description: "Create a free UniUI account for 5 questions a day, course-grounded answers with real citations, past-question practice, and a study streak.",
    openGraph: {
      title: "UniUI – Begin Your Journey.",
      description: "Create a free UniUI account for 5 questions a day, course-grounded answers with real citations, past-question practice, and a study streak.",
      url: `${SITE_URL}/auth/signup`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  ask: {
    title: "UniUI – Ask a Question.",
    description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI – Ask a Question.",
      description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/ask`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  faq: {
    keywords: [
      "uniui-faq",
      "uniui faq",
      "uniui help",
      "uniui-about",
      "uniui courses",
      "frequently asked questions",
      "uniui support",
      "uniui.com.ng",
    ],
    title: "UniUI – FAQ.",
    description: "How the UniUI Socratic tutor works, what makes an answer verified, how citations and course grounding work, plus plans, credits and account help.",
    openGraph: {
      title: "UniUI – FAQ.",
      description: "How the UniUI Socratic tutor works, what makes an answer verified, how citations and course grounding work, plus plans, credits and account help.",
      url: `${SITE_URL}/faq`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  pricing: {
    keywords: [
      "uniui-pricing",
      "uniui pricing",
      "uniui plans",
      "uniui credits",
      "uniui-about",
      "student pricing nigeria",
      "free questions",
      "uniui.com.ng",
      "uniui subscription",
    ],
    title: "UniUI – Pricing.",
    description: "UniUI pricing for Nigerian students: 5 free questions a day, then simple credit packs and a student plan. Pay for verified answers, not promises.",
    openGraph: {
      title: "UniUI – Pricing.",
      description: "UniUI pricing for Nigerian students: 5 free questions a day, then simple credit packs and a student plan. Pay for verified answers, not promises.",
      url: `${SITE_URL}/pricing`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  about: {
    keywords: [
      "uniui-about",
      "uniui about",
      "about uniui",
      "uniui-community",
      "uniui courses",
      "ai academic companion",
      "socratic tutor nigeria",
      "nigerian university students",
      "academic ai",
      "uniui.com.ng",
    ],
    title: "UniUI – About.",
    description:
      "UniUI is the AI academic companion for Nigerian university students: Socratic answers grounded in your own uploaded course notes, with citations.",
    openGraph: {
      title: "UniUI – About.",
      description:
        "UniUI is the AI academic companion for Nigerian university students: Socratic answers grounded in your own uploaded course notes, with citations.",
      url: `${SITE_URL}/about`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  contact: {
    keywords: [
      "uniui-contact",
      "uniui contact",
      "contact uniui",
      "uniui-about",
      "uniui support",
      "uniui help",
      "student support nigeria",
      "uniui.com.ng",
    ],
    title: "UniUI – Contact.",
    description:
      "Questions, suggestions or need help with UniUI? Contact the admin team for support, or for help getting your materials uploaded and verified.",
    openGraph: {
      title: "UniUI – Contact.",
      description:
        "Questions, suggestions or need help with UniUI? Contact the admin team for support, or for help getting your materials uploaded and verified.",
      url: `${SITE_URL}/contact`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  privacy: {
    keywords: [
      "uniui-privacy",
      "uniui privacy policy",
      "uniui data protection",
      "uniui-about",
      "student data privacy",
      "uniui.com.ng",
    ],
    title: "UniUI – Privacy Policy.",
    description: "How UniUI protects your data: what we store, how conversations and uploaded materials are handled, how long we keep them, and your controls.",
    openGraph: {
      title: "UniUI – Privacy Policy.",
      description: "How UniUI protects your data: what we store, how conversations and uploaded materials are handled, how long we keep them, and your controls.",
      url: `${SITE_URL}/privacy`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  terms: {
    keywords: [
      "uniui-terms",
      "uniui terms of service",
      "uniui-about",
      "uniui usage",
      "student terms",
      "uniui.com.ng",
    ],
    title: "UniUI – Terms of Service.",
    description:
      "The UniUI terms of service: how you may use the platform, fair use of questions and credits, ownership of answers, and how changes are notified.",
    openGraph: {
      title: "UniUI – Terms of Service.",
      description:
        "The UniUI terms of service: how you may use the platform, fair use of questions and credits, ownership of answers, and how changes are notified.",
      url: `${SITE_URL}/terms`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  "tools-notes": {
    title: "UniUI – Notes.",
    description: "The UniUI terms of service: how you may use the platform, fair use of questions and credits, ownership of answers, and how changes are notified.",
    openGraph: {
      title: "UniUI – Notes.",
      description: "The UniUI terms of service: how you may use the platform, fair use of questions and credits, ownership of answers, and how changes are notified.",
      url: `${SITE_URL}/tools/notes`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  pay: {
    title: "UniUI – Payments.",
    description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI – Payments.",
      description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/pay`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  "rate-limited": {
    title: "UniUI – Rate Limited.",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI – Rate Limited.",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/rate-limited`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  maintenance: {
    title: "UniUI – System Maintenance.",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI – System Maintenance.",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/maintenance`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  events: {
    title: "UniUI – Events.",
    description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI – Events.",
      description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/events`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  courses: {
    keywords: [
      "uniui-courses",
      "uniui courses",
      "uniui course catalogue",
      "course materials",
      "past questions",
      "lecture notes",
      "uniui-about",
      "uniui-community",
      "nigerian university courses",
      "uniui.com.ng",
    ],
    title: "UniUI – Course Catalogue.",
    description: "Browse every UniUI course by department, level and semester. See the notes, past questions and materials already uploaded, and start asking.",
    openGraph: {
      title: "UniUI – Course Catalogue.",
      description: "Browse every UniUI course by department, level and semester. See the notes, past questions and materials already uploaded, and start asking.",
      url: `${SITE_URL}/courses`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  departments: {
    title: "UniUI – Departments.",
    description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI – Departments.",
      description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/departments`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  learn: {
    title: "UniUI – Learning Paths.",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI – Learning Paths.",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/learn`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  accessibility: {
    keywords: [
      "uniui-accessibility",
      "accessibility at uniui",
      "uniui-about",
      "uniui inclusive design",
      "uniui.com.ng",
    ],
    title: "UniUI – Accessibility.",
    description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI – Accessibility.",
      description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/accessibility`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  error: {
    title: "UniUI – Something Went Wrong.",
    description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI – Something Went Wrong.",
      description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/error`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  "500": {
    title: "UniUI – Server Error.",
    description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI – Server Error.",
      description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/500`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  "404": {
    title: "UniUI – Page Not Found.",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI – Page Not Found.",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/404`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  leaderboard: {
    title: "UniUI – Leaderboard.",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI – Leaderboard.",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/leaderboard`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  changelog: {
    title: "UniUI – Changelog.",
    description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI – Changelog.",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/changelog`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  help: {
    title: "UniUI – Help Center.",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI – Help Center.",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/help`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  "status-videos": {
    title: "UniUI – Status & Videos.",
    description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI – Status & Videos.",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/status-videos`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  conversations: {
    title: "UniUI – Conversations.",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI – Conversations.",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/conversations`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  "study-groups": {
    title: "UniUI – Study Groups.",
    description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI – Study Groups.",
      description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/study-groups`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  community: {
    keywords: [
      "uniui-community",
      "uniui community",
      "uniui-about",
      "uniui courses",
      "student community nigeria",
      "study group",
      "uniui forum",
      "university students",
      "peer learning",
      "uniui.com.ng",
    ],
    title: "UniUI – Community.",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI – Community.",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/community`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  "403": {
    title: "UniUI – Access Denied.",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI – Access Denied.",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/403`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  offline: {
    title: "UniUI – No Connection.",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI – No Connection.",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/offline`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  success: {
    title: "UniUI - Done.",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI - Done.",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/success`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  loading: {
    title: "UniUI - Loading.",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI - Loading.",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/loading`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  dashboard: {
    title: "Dashboard — Academic Command Console",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI — Academic Dashboard",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/dashboard`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  profile: {
    title: "Student Academic Profile",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI — Student Profile",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/profile`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  settings: {
    title: "Platform Settings & Governance",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI — Platform Settings",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/settings`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  admin: {
    title: "Faculty Administration Console",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI — Faculty Administration",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/admin`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  "course-rep": {
    title: "Course Representative Hub",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI — Course Rep Hub",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/course-rep`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  calendar: {
    title: "Academic Calendar & Timetable",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI — Academic Calendar",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/calendar`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  "past-questions": {
    title: "Past Examination Questions & Verified Solutions",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI — Past Questions & Solutions",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/past-questions`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  "tools-flashcards": {
    title: "Spaced Repetition Flashcards",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI — Spaced Repetition Flashcards",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/tools/flashcards`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  "tools-gpa-calculator": {
    title: "Academic GPA & CGPA Calculator",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI — GPA & CGPA Calculator",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/tools/gpa-calculator`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  "tools-formula-sheet": {
    title: "Departmental Formula & Theorem Vault",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI — Formula Sheet Vault",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/tools/formula-sheet`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  "tools-pdf-summarizer": {
    title: "Academic Material & PDF Summarizer",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI — Material Summarizer",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/tools/pdf-summarizer`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  "tools-citation-generator": {
    title: "Academic Citation & Reference Generator",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI — Citation Generator",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/tools/citation-generator`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  "tools-schedule-planner": {
    title: "Study Schedule & Term Planner",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI — Study Schedule Planner",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/tools/schedule-planner`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  "tools-study-timer": {
    title: "Focused Pomodoro Study Timer",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI — Study Timer",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/tools/study-timer`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  "tools-whiteboard": {
    title: "Collaborative Academic Canvas",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI — Whiteboard",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/tools/whiteboard`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  "my-progress": {
    title: "Academic Mastery & Readiness Progress",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI — Academic Mastery Progress",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/my-progress`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  badges: {
    title: "Academic Honors & Achievement Badges",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI — Achievement Badges",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/badges`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  quests: {
    title: "Daily Academic Challenges & Quests",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI — Academic Quests",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/quests`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
  tutoring: {
    title: "Peer & Faculty Tutoring Sessions",
    description:
      "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    openGraph: {
      title: "UniUI — Peer Tutoring",
      description:
        "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
      url: `${SITE_URL}/tutoring`,
      siteName: SITE_NAME,
      type: "website",
    },
  },
};

export function getMetadata(key: string): MetadataPreset {
  return METADATA[key] || METADATA.landing;
}
export const metadataPresets = {
  home: {
    keywords: [
      "uniui",
      "uniui-home",
      "uniui about",
      "uniui community",
      "uniui courses",
      "socratic ai tutor",
      "ai tutor for nigerian students",
      "verified answers",
      "study tools",
      "university ai assistant",
      "uniui.com.ng",
      "learn with uniui",
    ],
    title: { default: "UniUI — Unified Academic Intelligence Platform", template: "%s | UniUI" },
    description: "UniUI is a strict Socratic tutor for Nigerian university students. Ask anything from your course notes and get a verified, cited, step-by-step answer.",
    creator: "UniUI Academic Network",
    robots: { index: true, follow: true, googleBot: { index: true, follow: true, "max-image-preview": "large", "max-snippet": -1, "max-video-preview": -1 } },
    openGraph: { type: "website", locale: "en_US", url: "https://app.uniui.com.ng", siteName: "UniUI", title: "UniUI — Unified Academic Intelligence Platform", description: "UniUI is an AI-powered academic platform for Nigerian university students.", images: [{ url: "https://app.uniui.com.ng/UNIUI_BotFather_640x360.png", width: 640, height: 360, alt: "UniUI" }] },
    twitter: { card: "summary_large_image", title: "UniUI — Unified Academic Intelligence Platform", description: "UniUI is an AI-powered academic platform for Nigerian university students.", images: ["https://app.uniui.com.ng/UNIUI_BotFather_640x360.png"] },
  },
  notes: {
    title: { default: "Notes | UniUI Study Tools", template: "%s | UniUI" },
    description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    keywords: ["study notes", "AI notes", "academic notes", "UniUI"],
    authors: [{ name: "UniUI Academic Network" }],
    robots: { index: true, follow: true },
    openGraph: { title: "Notes | UniUI Study Tools", description: "Access AI-enriched study notes organized by subject and topic.", url: "https://app.uniui.com.ng/tools/notes", siteName: "UniUI", type: "website" },
    twitter: { card: "summary_large_image", title: "Notes | UniUI Study Tools", description: "Access AI-enriched study notes organized by subject and topic." },
  },
  tools: {
    title: { default: "Study Tools | UniUI", template: "%s | UniUI" },
    description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    keywords: ["study tools", "CGPA", "UniUI"],
    authors: [{ name: "UniUI Academic Network" }],
    robots: { index: true, follow: true },
    openGraph: { title: "Study Tools | UniUI", description: "Everything you need to dominate your CGPA.", url: "https://app.uniui.com.ng/tools", siteName: "UniUI", type: "website" },
    twitter: { card: "summary_large_image", title: "Study Tools | UniUI", description: "Everything you need to dominate your CGPA." },
  },
  about: {
    keywords: [
      "uniui-about",
      "uniui about",
      "about uniui",
      "uniui-community",
      "uniui courses",
      "ai academic companion",
      "socratic tutor nigeria",
      "nigerian university students",
      "academic ai",
      "uniui.com.ng",
    ],
    title: { default: "About Us | UniUI", template: "%s | UniUI" },
    description: "UniUI is the AI academic companion for Nigerian university students: Socratic answers grounded in your own uploaded course notes, with citations.",
    robots: { index: true, follow: true },
    openGraph: { title: "About Us | UniUI", description: "Learn about UniUI's mission to empower Nigerian university students.", url: "https://app.uniui.com.ng/about", siteName: "UniUI", type: "website" },
    twitter: { card: "summary_large_image", title: "About Us | UniUI", description: "Learn about UniUI's mission to empower Nigerian university students." },
  },
  contact: {
    keywords: [
      "uniui-contact",
      "uniui contact",
      "contact uniui",
      "uniui-about",
      "uniui support",
      "uniui help",
      "student support nigeria",
      "uniui.com.ng",
    ],
    title: { default: "Contact Us | UniUI", template: "%s | UniUI" },
    description: "Questions, suggestions or need help with UniUI? Contact the admin team for support, or for help getting your materials uploaded and verified.",
    robots: { index: true, follow: true },
    openGraph: { title: "Contact Us | UniUI", description: "Get in touch with UniUI support and feedback.", url: "https://app.uniui.com.ng/contact", siteName: "UniUI", type: "website" },
    twitter: { card: "summary_large_image", title: "Contact Us | UniUI", description: "Get in touch with UniUI support and feedback." },
  },
  community: {
    keywords: [
      "uniui-community",
      "uniui community",
      "uniui-about",
      "uniui courses",
      "student community nigeria",
      "study group",
      "uniui forum",
      "university students",
      "peer learning",
      "uniui.com.ng",
    ],
    title: { default: "Community | UniUI", template: "%s | UniUI" },
    description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    robots: { index: true, follow: true },
    openGraph: { title: "Community | UniUI", description: "Join the UniUI community and connect with fellow students.", url: "https://app.uniui.com.ng/community", siteName: "UniUI", type: "website" },
    twitter: { card: "summary_large_image", title: "Community | UniUI", description: "Join the UniUI community and connect with fellow students." },
  },
  faq: {
    keywords: [
      "uniui-faq",
      "uniui faq",
      "uniui help",
      "uniui-about",
      "uniui courses",
      "frequently asked questions",
      "uniui support",
      "uniui.com.ng",
    ],
    title: { default: "FAQ | UniUI", template: "%s | UniUI" },
    description: "How the UniUI Socratic tutor works, what makes an answer verified, how citations and course grounding work, plus plans, credits and account help.",
    robots: { index: true, follow: true },
    openGraph: { title: "FAQ | UniUI", description: "Frequently asked questions about UniUI.", url: "https://app.uniui.com.ng/faq", siteName: "UniUI", type: "website" },
    twitter: { card: "summary_large_image", title: "FAQ | UniUI", description: "Frequently asked questions about UniUI." },
  },
  pricing: {
    keywords: [
      "uniui-pricing",
      "uniui pricing",
      "uniui plans",
      "uniui credits",
      "uniui-about",
      "student pricing nigeria",
      "free questions",
      "uniui.com.ng",
      "uniui subscription",
    ],
    title: { default: "Pricing | UniUI", template: "%s | UniUI" },
    description: "UniUI pricing for Nigerian students: 5 free questions a day, then simple credit packs and a student plan. Pay for verified answers, not promises.",
    robots: { index: true, follow: true },
    openGraph: { title: "Pricing | UniUI", description: "UniUI pricing for Nigerian students: 5 free questions daily, then simple credit packs and a student plan. Pay for verified answers, never for promises. No hidden fees.", url: "https://app.uniui.com.ng/pricing", siteName: "UniUI", type: "website" },
    twitter: { card: "summary_large_image", title: "Pricing | UniUI", description: "UniUI pricing for Nigerian students: 5 free questions daily, then simple credit packs and a student plan. Pay for verified answers, never for promises. No hidden fees." },
  },
  courses: {
    keywords: [
      "uniui-courses",
      "uniui courses",
      "uniui course catalogue",
      "course materials",
      "past questions",
      "lecture notes",
      "uniui-about",
      "uniui-community",
      "nigerian university courses",
      "uniui.com.ng",
    ],
    title: { default: "Courses | UniUI", template: "%s | UniUI" },
    description: "Browse every UniUI course by department, level and semester. See the notes, past questions and materials already uploaded, and start asking.",
    robots: { index: true, follow: true },
    openGraph: { title: "Courses | UniUI", description: "Browse available courses and start learning today.", url: "https://app.uniui.com.ng/courses", siteName: "UniUI", type: "website" },
    twitter: { card: "summary_large_image", title: "Courses | UniUI", description: "Browse available courses and start learning today." },
  },
  learn: {
    title: { default: "Learn | UniUI", template: "%s | UniUI" },
    description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    keywords: ["learn", "tutor", "AI", "UniUI"],
    authors: [{ name: "UniUI Academic Network" }],
    robots: { index: true, follow: true },
    openGraph: { title: "Learn | UniUI", description: "Personalized AI tutor & mastery tracker.", url: "https://app.uniui.com.ng/learn", siteName: "UniUI", type: "website" },
    twitter: { card: "summary_large_image", title: "Learn | UniUI", description: "Personalized AI tutor & mastery tracker." },
  },
  pastQuestions: {
    title: { default: "Past Questions | UniUI", template: "%s | UniUI" },
    description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    keywords: ["past questions", "exams", "UniUI"],
    authors: [{ name: "UniUI Academic Network" }],
    robots: { index: true, follow: true },
    openGraph: { title: "Past Questions | UniUI", description: "Access past exam questions and practice for success.", url: "https://app.uniui.com.ng/past-questions", siteName: "UniUI", type: "website" },
    twitter: { card: "summary_large_image", title: "Past Questions | UniUI", description: "Access past exam questions and practice for success." },
  },
  badges: {
    title: { default: "Badges | UniUI", template: "%s | UniUI" },
    description: "UniUI is the AI academic companion for Nigerian university students, giving verified, cited, step-by-step answers grounded in your course materials.",
    keywords: ["badges", "achievements", "UniUI"],
    authors: [{ name: "UniUI Academic Network" }],
    robots: { index: true, follow: true },
    openGraph: { title: "Badges | UniUI", description: "Earn badges and track your academic achievements.", url: "https://app.uniui.com.ng/badges", siteName: "UniUI", type: "website" },
    twitter: { card: "summary_large_image", title: "Badges | UniUI", description: "Earn badges and track your academic achievements." },
  },
};
